import { useCallback, useEffect, useState } from 'react';
import { getLeads } from '../services/CRM/leadsService';
import { useAuthenticatedUserInfo } from '../services/Contexts/AuthenticatedUserInfoContext';
import { FOLLOW_UP_ALERTS_REFRESH_EVENT } from '../utils/followUpAlertsEvents';

const POLL_INTERVAL = 5 * 60 * 1000;
const SNOOZE_KEY = 'followUpBannerSnoozedUntil';
const SNOOZE_MS = 60 * 60 * 1000;

export const isFollowUpSnoozed = () => {
	const until = localStorage.getItem(SNOOZE_KEY);
	return !!until && Date.now() < Number(until);
};

export const getUpcomingSoon = (leads, windowMs = 60 * 60 * 1000) => {
	const now = Date.now();
	const cutoff = now + windowMs;
	return leads
		.map((lead) => {
			const soonFUs = (lead.followUps || []).filter((f) => {
				if (f.isCompleted) return false;
				const t = new Date(f.dateTime).getTime();
				return t >= now && t <= cutoff;
			});
			return soonFUs.length > 0 ? { lead, followUps: soonFUs } : null;
		})
		.filter(Boolean);
};

export const getOverdue = (leads) => {
	const now = Date.now();
	return leads
		.map((lead) => {
			const overdueFUs = (lead.followUps || []).filter(
				(f) => !f.isCompleted && new Date(f.dateTime).getTime() < now,
			);
			return overdueFUs.length > 0
				? { lead, followUps: overdueFUs, count: overdueFUs.length }
				: null;
		})
		.filter(Boolean);
};

export const formatFollowUpTime = (date) =>
	new Date(date).toLocaleTimeString(undefined, {
		hour: '2-digit',
		minute: '2-digit',
	});

export const useFollowUpAlerts = () => {
	const { companyId } = useAuthenticatedUserInfo();
	const [upcoming, setUpcoming] = useState([]);
	const [overdue, setOverdue] = useState([]);
	const [dismissed, setDismissed] = useState(isFollowUpSnoozed);
	const [loading, setLoading] = useState(false);

	const check = useCallback(async ({ silent = false } = {}) => {
		if (!companyId) return;
		if (!silent) {
			setLoading(true);
		}
		try {
			const leads = await getLeads(companyId);
			setUpcoming(getUpcomingSoon(leads));
			setOverdue(getOverdue(leads));
			if (!isFollowUpSnoozed()) {
				setDismissed(false);
			}
		} catch {
			// non-critical
		} finally {
			if (!silent) {
				setLoading(false);
			}
		}
	}, [companyId]);

	useEffect(() => {
		check();
		const pollId = setInterval(() => check({ silent: true }), POLL_INTERVAL);
		return () => clearInterval(pollId);
	}, [check]);

	useEffect(() => {
		const refresh = () => check({ silent: true });
		window.addEventListener(FOLLOW_UP_ALERTS_REFRESH_EVENT, refresh);
		return () => window.removeEventListener(FOLLOW_UP_ALERTS_REFRESH_EVENT, refresh);
	}, [check]);

	const snooze = useCallback(() => {
		localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
		setDismissed(true);
	}, []);

	const totalOverdueFUs = overdue.reduce((sum, item) => sum + item.count, 0);
	const totalUpcomingFUs = upcoming.reduce((sum, item) => sum + item.followUps.length, 0);
	const hasOverdue = overdue.length > 0;
	const hasUpcoming = upcoming.length > 0;
	const hasAlerts = (hasOverdue || hasUpcoming) && !dismissed;
	const totalCount = totalOverdueFUs + totalUpcomingFUs;

	return {
		upcoming,
		overdue,
		loading,
		hasAlerts,
		hasOverdue,
		hasUpcoming,
		totalOverdueFUs,
		totalUpcomingFUs,
		totalCount,
		snooze,
		refresh: check,
	};
};
